import React from "react";
import ArticleActionsMenu from "./ArticleActionsMenu";
import { formatDate } from "../../utils/date.utils";
import { GrantsLabelItem, ArticleLabelMap } from "../../services/grantsApi";
import LabelCell from "./labels/LabelCell";
import { getColorConfig } from "./labels/labelColors";

interface ArticleListItem {
  id: string;
  title: string;
  publicationDate: string;
  excerpt: string;
  tags: string[];
  hasKfsFlag: boolean;
  url: string;
  office: {
    district: string;
    name: string;
    provinceName: string;
    province: string;
    locality: string;
  };
}

interface SortConfig {
  column: "publicationDate";
  order: "ASC" | "DESC";
}

interface KfsArticlesTableProps {
  articles: ArticleListItem[];
  onSortChange?: (sortBy: string, sortOrder: string) => void;
  currentSort?: SortConfig | null;
  onProvinceClick?: (province: string) => void;
  userRole?: string;
  onReprocess?: (
    articleId: string | number,
    type: "kfs" | "bur"
  ) => Promise<void>;
  onReport?: (articleId: string | number, type: "kfs" | "bur") => void;
  labels?: GrantsLabelItem[];
  articleLabels?: ArticleLabelMap;
  onSetArticleLabel?: (articleId: string, labelId: string | null) => Promise<any>;
}

const KfsArticlesTable: React.FC<KfsArticlesTableProps> = ({
  articles,
  onSortChange,
  currentSort,
  onProvinceClick,
  userRole,
  onReprocess,
  onReport,
  labels = [],
  articleLabels = {},
  onSetArticleLabel,
}) => {
  const handleSort = (column: string) => {
    if (!onSortChange) return;

    let order = "DESC";
    if (currentSort?.column === column && currentSort.order === "DESC") {
      order = "ASC";
    }

    onSortChange(column, order);
  };

  const getSortIcon = (column: string) => {
    if (currentSort?.column !== column) {
      return (
        <svg
          className="w-4 h-4 ml-1 text-neutral-400"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      );
    }

    return currentSort.order === "ASC" ? (
      <svg
        className="w-4 h-4 ml-1 text-primary-600"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
          clipRule="evenodd"
        />
      </svg>
    ) : (
      <svg
        className="w-4 h-4 ml-1 text-primary-600"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
          clipRule="evenodd"
        />
      </svg>
    );
  };

  const getTagBadgeColor = (tag: string) => {
    const tagLower = tag.toLowerCase();
    if (tagLower.includes("szkolenie") || tagLower.includes("kurs")) {
      return "bg-blue-100 text-blue-800";
    }
    if (tagLower.includes("dofinansowanie") || tagLower.includes("dotacja")) {
      return "bg-green-100 text-green-800";
    }
    if (tagLower.includes("nabór") || tagLower.includes("rekrutacja")) {
      return "bg-purple-100 text-purple-800";
    }
    if (tagLower.includes("rezerwa")) {
      return "bg-orange-100 text-orange-800";
    }
    return "bg-neutral-100 text-neutral-700";
  };

  const removeWojewodztwoPrefix = (provinceName: string): string => {
    return provinceName.replace(/^Województwo\s+/i, "").trim();
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-neutral-200">
        <thead className="bg-neutral-50">
          <tr>
            <th
              className="px-2 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 w-24"
              onClick={() => handleSort("publicationDate")}
            >
              <div className="flex items-center">
                Data publ.
                {getSortIcon("publicationDate")}
              </div>
            </th>
            <th className="px-2 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider w-20">
              Województwo
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider w-1/4">
              Urząd pracy
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
              Tytuł i tagi
            </th>
            {labels.length > 0 && (
              <th className="px-2 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider w-20 md:w-28">
                Etykieta
              </th>
            )}
            <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider w-16">
              Akcje
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-neutral-200">
          {articles.map((article) => {
            const artLabel = articleLabels[article.id];
            const rowColor = artLabel ? getColorConfig(artLabel.color) : null;
            return (
            <tr
              key={article.id}
              className={rowColor ? `${rowColor.rowBg}` : "hover:bg-neutral-50"}
            >
              <td className="px-2 py-2 whitespace-nowrap text-sm text-left text-neutral-500">
                {formatDate(article.publicationDate)}
              </td>
              <td className="px-3 py-2 whitespace-nowrap text-sm text-left">
                <button
                  onClick={() => onProvinceClick?.(article.office.province)}
                  className="text-primary-600 hover:text-primary-800 hover:underline cursor-pointer"
                  title={`Filtruj według województwa: ${removeWojewodztwoPrefix(article.office.provinceName)}`}
                >
                  {removeWojewodztwoPrefix(article.office.provinceName)}
                </button>
              </td>
              <td className="px-3 py-2 text-sm text-left max-w-xs">
                <div className="flex flex-col">
                  <span
                    className="text-neutral-900 font-medium truncate"
                    title={article.office.name}
                  >
                    {article.office.name}
                  </span>
                  <span
                    className="text-xs text-neutral-500 truncate"
                    title={article.office.locality}
                  >
                    {article.office.locality}
                  </span>
                </div>
              </td>
              <td className="px-3 py-2 text-sm text-left text-neutral-900">
                <div className="flex flex-col">
                  <div className="flex flex-wrap items-center gap-1 mb-1">
                    {article.tags.slice(0, 3).map((tag, index) => (
                      <span
                        key={index}
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getTagBadgeColor(tag)}`}
                      >
                        {tag}
                      </span>
                    ))}
                    {article.tags.length > 2 && (
                      <span className="text-xs text-neutral-500">
                        +{article.tags.length - 2}
                      </span>
                    )}
                  </div>
                  <a
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-800 hover:underline font-medium max-w-lg truncate block"
                    title={article.title}
                  >
                    {(() => {
                      const title = article.title;
                      return title.length > 100
                        ? `${title.substring(0, 100)}...`
                        : title;
                    })()}
                  </a>
                </div>
              </td>
              {labels.length > 0 && (
                <td className="px-2 py-2 whitespace-nowrap text-sm">
                  {onSetArticleLabel && (
                    <LabelCell
                      articleId={article.id}
                      labels={labels}
                      currentLabel={artLabel || null}
                      onSetLabel={onSetArticleLabel}
                    />
                  )}
                </td>
              )}
              <td className="px-3 py-2 whitespace-nowrap text-sm">
                {onReprocess && onReport && (
                  <ArticleActionsMenu
                    articleId={article.id}
                    articleType="kfs"
                    articleTitle={article.title}
                    articleUrl={article.url}
                    isAdmin={userRole === "admin"}
                    onReprocess={onReprocess}
                    onReport={onReport}
                  />
                )}
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default KfsArticlesTable;
